import React, { useState, useEffect } from 'react';

/**
 * Coordinate Decoder Component
 * Shows the 10-letter coordinate grid (e.g., XOCHIMILCO) that fills in as users solve locations
 * Animates letters from the answer into their correct positions
 */
export function CoordinateDecoder({ tour, solvedSteps, justSolvedStep, onAnimationComplete }) {
  const [animatingLetter, setAnimatingLetter] = useState(null);
  const answerLength = tour.finalAnswerLength || 10;

  // Build the current state of the coordinate grid
  const coordinateGrid = Array(answerLength).fill('_');
  solvedSteps.forEach((step) => {
    if (step.revealedLetter && step.secretIndex !== undefined) {
      coordinateGrid[step.secretIndex] = step.revealedLetter;
    }
  });

  // Check if all coordinates are deciphered
  const allDeciphered = coordinateGrid.every(letter => letter !== '_');

  // Trigger animation when a new step is solved
  useEffect(() => {
    if (justSolvedStep && justSolvedStep.revealedLetter) {
      setAnimatingLetter({
        letter: justSolvedStep.revealedLetter,
        position: justSolvedStep.secretIndex,
        fromAnswer: justSolvedStep.solution
      });

      // Clear animation after it completes
      const timer = setTimeout(() => {
        setAnimatingLetter(null);
        if (onAnimationComplete) onAnimationComplete();
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [justSolvedStep, onAnimationComplete]);

  return (
    <div className="space-y-4">
      {/* Title */}
      <div className="text-center">
        <div
          className="text-xs font-mono font-bold uppercase tracking-widest mb-2 transition-colors duration-1000"
          style={{ color: allDeciphered ? '#EF4444' : '#10B981' }}
        >
          Location Decryption
        </div>
        <div className="text-sm font-mono text-gray-400">
          {solvedSteps.length} of {answerLength} fragments decoded
        </div>
      </div>

      {/* Coordinate Grid */}
      <div className="flex justify-center gap-1.5 sm:gap-2">
        {coordinateGrid.map((letter, idx) => {
          const isAnimating = animatingLetter && animatingLetter.position === idx;
          const isFilled = letter !== '_';

          return (
            <div
              key={idx}
              className={`relative w-9 h-11 sm:w-10 sm:h-12 flex items-center justify-center rounded border-2 font-mono text-xl sm:text-2xl font-bold transition-all duration-1000 ${
                isAnimating ? 'letter-pop' : ''
              }`}
              style={{
                borderColor: isFilled ? (allDeciphered ? '#EF4444' : '#10B981') : '#334155',
                backgroundColor: isFilled ? (allDeciphered ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.1)') : 'rgba(51, 65, 85, 0.3)',
                color: isFilled ? (allDeciphered ? '#EF4444' : '#10B981') : '#64748b',
                letterSpacing: '0.05em'
              }}
            >
              {letter}
            </div>
          );
        })}
      </div>

      {/* Animation styles */}
      <style>{`
        .letter-pop {
          animation: letterPop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        @keyframes letterPop {
          0% {
            transform: scale(0.5) translateY(-20px);
            opacity: 0;
          }
          50% {
            transform: scale(1.3) translateY(-5px);
          }
          100% {
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
